import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, Hammer, Search, User } from 'lucide-react';
import Button from '../components/ui/Button';

const NewUserConfirmed: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-2xl">
        <div className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-20 w-20 rounded-full bg-green-100 mb-6">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Velkommen til DoMore!
            </h1>
            
            <p className="text-lg text-gray-700 mb-2">
              Din e-mail er nu bekræftet
            </p>
            
            <p className="text-base text-gray-600 mb-8">
              Du er klar til at dele og låne værktøj med andre i dit område. 
              Vælg hvordan du vil komme i gang:
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
              {/* Share Tools Card */}
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-6 border-2 border-green-200 hover:border-green-300 transition-all">
                <div className="flex flex-col items-center text-center">
                  <div className="bg-green-600 rounded-full p-3 mb-4">
                    <Hammer className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Del dit værktøj
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Tilføj dit værktøj og begynd at tjene penge ved at udleje det
                  </p>
                  <Link to="/dashboard" className="w-full">
                    <Button variant="primary" fullWidth>
                      Kom i gang
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Borrow Tools Card */}
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-6 border-2 border-blue-200 hover:border-blue-300 transition-all">
                <div className="flex flex-col items-center text-center">
                  <div className="bg-blue-600 rounded-full p-3 mb-4">
                    <Search className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Lån værktøj
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Find det værktøj du har brug for i dit nærområde
                  </p>
                  <Link to="/browse" className="w-full">
                    <Button variant="outline" fullWidth>
                      Find værktøj
                    </Button>
                  </Link>
                </div>
              </div>

              {/* Complete Profile Card */}
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-6 border-2 border-purple-200 hover:border-purple-300 transition-all">
                <div className="flex flex-col items-center text-center">
                  <div className="bg-purple-600 rounded-full p-3 mb-4">
                    <User className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    Udfyld din profil
                  </h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Tilføj information om dig selv og byg tillid
                  </p>
                  <Link to="/settings" className="w-full">
                    <Button variant="outline" fullWidth>
                      Rediger profil
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                Næste skridt
              </h3>
              <div className="text-left space-y-3 max-w-xl mx-auto">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-green-600 font-semibold text-sm">1</span>
                  </div>
                  <p className="text-sm text-gray-700">
                    <strong>Tilføj dit første værktøj</strong> og angiv udlejningspris og tilgængelighed
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-green-600 font-semibold text-sm">2</span>
                  </div>
                  <p className="text-sm text-gray-700">
                    <strong>Udfyld din profil</strong> med billede og beskrivelse for at bygge tillid
                  </p>
                </div>
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-6 w-6 rounded-full bg-green-100 flex items-center justify-center mr-3 mt-0.5">
                    <span className="text-green-600 font-semibold text-sm">3</span>
                  </div>
                  <p className="text-sm text-gray-700">
                    <strong>Udforsk platformen</strong> og find værktøj du selv kan låne
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Har du brug for hjælp til at komme i gang?{' '}
            <Link to="/help" className="font-medium text-green-600 hover:text-green-500">
              Besøg vores Hjælpecenter
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default NewUserConfirmed;
